import React, { useState } from "react";
import {
  Card,
  CardContent,
} from "@/shared/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/shared/ui/table"
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "@/shared/ui/popover"
import { GradientBorder } from "@/shared/components/GradientBorder"
import { Trash2, UserRoundX, ChevronDown } from "lucide-react";
import { useSelector } from "react-redux";
import { selectUserData } from "@/api/rtk/userSelectors"
import { toast } from "react-toastify";
import Loader from '@/shared/components/Loader';
import { useTranslation } from "react-i18next";
import { getAvailableRoles } from "@/features/profile/members/getAvailableRoles"
import { AddMemberButton } from "@/features/profile/members/AddMemberButton"
import { useGetMembersQuery } from "@/api/rtk/profileApi"
import { useDeleteUserMutation, useUpdateUserRoleMutation } from "@/api/rtk/mutationsApi"

export function MembersTable({ showNewUserForm, setShowNewUserForm }) {
  const { t } = useTranslation();
  const { data } = useGetMembersQuery();
  const members = data?.members || [];
  const currentUser = useSelector(selectUserData);
  const [deleteUser] = useDeleteUserMutation();
  const [updateUserRole] = useUpdateUserRoleMutation();
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [deletingUsers, setDeletingUsers] = useState(new Set());

  const sortedMembers = [...members].sort((a, b) => {
    if (a.id === currentUser.id) return -1;
    if (b.id === currentUser.id) return 1;
    return b.is_admin - a.is_admin;
  });

  const handleUpdateUserRole = async (id, role) => {
    setLoadingUserId(id);
    try {
      await updateUserRole({ userId:id, role }).unwrap();
      toast.success(t("profile.members.updateRole"));
    } catch {
      toast.error('Failed to update member role. Please try again.');
    }
    setLoadingUserId(null);
  };

  const handleDeleteUser = async (id) => {
    setDeletingUsers((prev) => new Set([...prev, id]));
    try {
      await deleteUser({ id }).unwrap();
      toast.success(t("profile.members.removeMember"));
    } catch (error) {
      const message = error?.data?.message || error?.data;
      if (message === 'Cannot delete the last admin') {
        toast.error(t("profile.members.cannotRemoveLastAdmin"));
      } else {
        toast.error(t("profile.members.failedToRemove"));
      }
    }
    setDeletingUsers((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  if (sortedMembers.length > 0) {
    return (
      <CardContent className="px-0 p-0">
        <Card className="rounded-2xl">
          <CardContent className="px-0 p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="font-semibold text-[#71717A] pr-0 w-[7%]">
                    ID
                  </TableCell>
                  <TableCell className="font-semibold text-[#71717A]">
                    {t("common.name")}
                  </TableCell>
                  <TableCell className="font-semibold text-[#71717A]">
                    {t("profile.members.role")}
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedMembers.map((member, index) => (
                  <TableRow key={member.id}>
                    <TableCell className="text-[#71717A]">
                      {index + 1}
                    </TableCell>
                    <TableCell className='w-[140px] min-w-[140px] xs:w-fit xs:min-w-fit'>{member.name}</TableCell>
                    <TableCell className='w-[140px] min-w-[140x] xs:w-fit xs:min-w-fit'>
                      {(currentUser.is_admin && member.id !== currentUser.id) ? (
                        <Popover open={loadingUserId === member.id} onOpenChange={(open) => setLoadingUserId(open ? member.id : null)}>
                          <PopoverTrigger asChild>
                            <div className="flex items-center gap-1 text-sm cursor-pointer">
                              {member.is_admin ? t("profile.members.roles.admin") : t(`profile.members.roles.${member.role}`)}
                              <ChevronDown size={16} />
                            </div>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            className="w-fit p-1 text-sm flex flex-col justify-center font-SuisseIntlLight"
                            disabled={loadingUserId === member.id}
                          >
                            {getAvailableRoles(member).map((role) => (
                              <CardContent
                                key={role}
                                className="px-4 py-2 cursor-pointer hover:bg-slate-100 font-SuisseIntlLight hover:text-slate-900"
                                onClick={() => handleUpdateUserRole(member.id, role)}
                              >
                                {t(`profile.members.roles.${role}`)}
                              </CardContent>
                            ))}
                          </PopoverContent>
                        </Popover>
                      ) : (
                        <span>{member.is_admin ? "Admin" : "Member"}</span>
                      )}
                      </TableCell>
                      <TableCell className="align-end pr-4">
                        {(currentUser.is_admin && member.id !== currentUser.id) && (
                              deletingUsers.has(member.id) ? 
                                  <Loader size="18px" className="text-[#71717a]" />
                                :
                                  <Trash2
                                    className="text-[#71717a] text-sm cursor-pointer"
                                    width={18}
                                    height={18}
                                    onClick={() => handleDeleteUser(member.id)}
                                  />
                          )}
                        </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </CardContent>
    );
  }

  return (
    <CardContent className="px-0 p-0">
      <Card className="rounded-2xl">
        <CardContent className="p-3 h-96 flex justify-center items-center flex-col">
          <GradientBorder>
            <div className="shadow-sm bg-white rounded-full px-4 py-4">
              <UserRoundX size={26} className="text-gray-600"/>
            </div>
          </GradientBorder>
          <h1 className="font-semibold font-SuisseIntlLight mt-[16px] md:text-[24px] text-[20px] text-center">
            {t("profile.members.noMembers")}
          </h1>
          <p className="mt-[4px] text-[#505050] my-3 text-[14px] font-SuisseIntlLight font-normal">
            {t("profile.members.addMembers")}
          </p>
          <AddMemberButton 
            showNewUserForm={showNewUserForm}  
            setShowNewUserForm={setShowNewUserForm}
          />
        </CardContent>
      </Card>
    </CardContent>
  );
}
